import { ROUTE } from "../../../common/ROUTE";
import type { ImageProps } from "../../types";

export const OCTANT_SUBPAGES = [
  { slug: "how-it-works", label: "How It Works" },
  { slug: "connect", label: "Connect" },
  { slug: "clarity", label: "Clarity" },
  { slug: "pulse", label: "Pulse" },
  { slug: "pricing", label: "Pricing" },
  { slug: "open-source", label: "Open Source" },
] as const;

export const OCTANT_PRODUCT_SLUGS = OCTANT_SUBPAGES.map((page) => page.slug) as [
  (typeof OCTANT_SUBPAGES)[number]["slug"],
  ...(typeof OCTANT_SUBPAGES)[number]["slug"][],
];

export type OctantProductSlug = (typeof OCTANT_PRODUCT_SLUGS)[number];
export type OctantPageTemplateKey =
  | "about-style"
  | "solution-style"
  | "open-source-style"
  | "pricing-style";
export type OctantTemplateId = 1 | 2 | 3 | 4;

export const OCTANT_PARENT_TEMPLATE_ID: OctantTemplateId = 1;

const OCTANT_PRODUCT_SLUG_SET = new Set<string>(OCTANT_PRODUCT_SLUGS);

const OCTANT_TEMPLATE_KEY_BY_ID: Record<OctantTemplateId, OctantPageTemplateKey> = {
  1: "about-style",
  2: "solution-style",
  3: "open-source-style",
  4: "pricing-style",
};

const OCTANT_TEMPLATE_ID_BY_SLUG: Record<OctantProductSlug, OctantTemplateId> = {
  "how-it-works": 2,
  connect: 2,
  clarity: 2,
  pulse: 2,
  pricing: 4,
  "open-source": 3,
};

const OCTANT_LABEL_BY_SLUG: Record<OctantProductSlug, string> =
  OCTANT_SUBPAGES.reduce<Record<OctantProductSlug, string>>((accum, subpage) => {
    accum[subpage.slug] = subpage.label;
    return accum;
  }, {} as Record<OctantProductSlug, string>);

const OCTANT_PRODUCT_META: Record<
  OctantProductSlug,
  { title: string; description: string; noIndex: boolean }
> = {
  "how-it-works": {
    title: "MyDecisive | Octant How It Works: Wire-Layer Telemetry Control",
    description:
      "See how Octant executes composable applets on the wire to transform telemetry streams into real-time control actions.",
    noIndex: false,
  },
  connect: {
    title: "MyDecisive | Octant Connect: Unified Telemetry Ingestion",
    description:
      "Connect telemetry sources, normalize signal flow, and route high-value observability data into autonomous SmartHub workflows.",
    noIndex: false,
  },
  clarity: {
    title: "MyDecisive | Octant Clarity: Signal Quality and Context",
    description:
      "Improve telemetry signal quality with contextual enrichment, filtering, and policy-driven transformations before data reaches downstream tools.",
    noIndex: false,
  },
  pulse: {
    title: "MyDecisive | Octant Pulse: Real-Time Operational Insight",
    description:
      "Track operational pulse in real time with wire-layer intelligence that surfaces meaningful patterns and suppresses observability noise.",
    noIndex: false,
  },
  pricing: {
    title: "MyDecisive | Octant Pricing: Scalable Telemetry Economics",
    description:
      "Review Octant pricing options designed for scalable telemetry control, lower ingestion costs, and predictable observability operations.",
    noIndex: false,
  },
  "open-source": {
    title: "MyDecisive | Octant Open Source: Build and Extend Applets",
    description:
      "Explore Octant open-source capabilities to build, extend, and integrate applets across your SmartHub telemetry control-plane environment.",
    noIndex: false,
  },
};

export interface OctantParentPageTemplate {
  hero: {
    title: string;
    subtitle: string;
    ctaText: string;
    ctaHref: string;
    image?: ImageProps;
  };
  capabilitiesSection: {
    title: string;
    cards: {
      title: string;
      body: string;
      image?: ImageProps;
      badge?: string;
      href?: string;
    }[];
  };
  modulesSection: {
    title: string;
    links: {
      label: string;
      href: string;
    }[];
  };
}

export const OCTANT_NAV_LINKS: { label: string; href: string }[] = [
  {
    label: "Octant Applets",
    href: ROUTE.OCTANT_PARENT,
  },
  ...OCTANT_SUBPAGES.filter((subpage) => subpage.slug !== "open-source").map((subpage) => ({
    label: subpage.label,
    href: buildOctantProductPath(subpage.slug),
  })),
];

export function buildOctantProductPath(slug: OctantProductSlug): string {
  return `${ROUTE.OCTANT_PARENT}/${slug}`;
}

export function getOctantProductPaths(): string[] {
  return OCTANT_PRODUCT_SLUGS.map((slug) => buildOctantProductPath(slug));
}

export function getOctantSubpageLabel(slug: OctantProductSlug): string {
  return OCTANT_LABEL_BY_SLUG[slug];
}

export function getOctantTemplateKeyForSlug(
  slug: OctantProductSlug,
): OctantPageTemplateKey {
  return OCTANT_TEMPLATE_KEY_BY_ID[OCTANT_TEMPLATE_ID_BY_SLUG[slug]];
}

export function getOctantTemplateIdForSlug(slug: OctantProductSlug): OctantTemplateId {
  return OCTANT_TEMPLATE_ID_BY_SLUG[slug];
}

export function isSupportedOctantSlug(
  slug: string | undefined,
): slug is OctantProductSlug {
  return typeof slug === "string" && OCTANT_PRODUCT_SLUG_SET.has(slug);
}

export function getOctantProductMeta(slug: string | undefined): {
  title: string;
  description: string;
  noIndex: boolean;
} | null {
  if (!isSupportedOctantSlug(slug)) {
    return null;
  }

  return OCTANT_PRODUCT_META[slug];
}
